import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BRAND_COLORS, type MeResponse, type PlayWindow } from '@lk/shared';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { TimerRing } from '../../components/TimerRing';
import { SyncIndicator } from '../../components/SyncIndicator';
import { ErrorState, LoadingState } from '../../components/States';
import { formatClock } from '../../lib/format';
import { playWindowQuery } from '../learner/queries';

const MINUTE_MS = 60_000;

/**
 * Обовʼязкова перерва після відрізка гри.
 *
 * Кінець перерви приходить із СЕРВЕРА і більше ніде не зберігається.
 * Раніше він лежав у localStorage, тобто перерву можна було скасувати
 * чисткою даних браузера, іншою вкладкою або приватним вікном. Тепер
 * єдине джерело правди — таблиця спроб на сервері: скільки б разів
 * дитина не перезавантажила сторінку, відлік той самий.
 */
export function BreakScreen({ me }: { me: MeResponse }): React.JSX.Element {
  const navigate = useNavigate();
  const learnerId = me.user.learnerId ?? me.learners[0]?.id ?? '';
  const playWindow = useQuery({ ...playWindowQuery(learnerId), enabled: Boolean(learnerId) });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  if (playWindow.isPending) return <LoadingState label="Дивимось на годинник…" />;

  const until = restEndsAt(playWindow.data, now);
  const remainingSec = Math.max(0, (until - now) / 1000);
  const done = remainingSec <= 0;
  const totalSec = Math.max(1, (playWindow.data?.breakMinutes ?? 0) * 60);

  /*
   * Сервер не відповів — і ми не знаємо, чи перерва скінчилась. Доки на
   * екрані тікає відлік, це не біда: перерву можна тільки подовжити, тож
   * лічити далі чесно. Але коли лічити вже нічого, «перерва скінчилась»
   * було б вигадкою — та ще й з активною кнопкою на арену, куди сервер
   * усе одно не пустить. Тому показуємо помилку як помилку.
   */
  if (done && playWindow.isError) {
    return (
      <main className="lk-shell lk-safe grid min-h-dvh place-items-center">
        <ErrorState
          error={playWindow.error}
          title="Не бачимо, чи скінчилась перерва"
          onRetry={() => void playWindow.refetch()}
        />
      </main>
    );
  }

  return (
    <main className="lk-shell lk-safe grid min-h-dvh place-items-center">
      <Panel className="w-full p-6 text-center">
        <Icon
          name={done ? 'energy' : 'lock'}
          size={88}
          label={done ? 'Перерва завершена' : 'Перерва триває'}
          className="lk-anim-float mx-auto"
        />
        <h1 className="mt-3 text-3xl font-black">{done ? 'Перерва скінчилась' : 'Перерва'}</h1>
        <p className="mt-2 text-sm font-semibold text-white/70">
          {done
            ? 'Мозок відпочив — можна повертатися на арену.'
            : 'Підведись, поглянь у вікно, попий води. Очі скажуть спасибі.'}
        </p>

        {done ? null : (
          <div className="mt-5 flex flex-col items-center gap-2">
            <TimerRing
              remaining={remainingSec}
              total={totalSec}
              size={132}
              label="До кінця перерви"
            />
            <p className="tabular text-lg font-black" style={{ color: BRAND_COLORS.cyan }}>
              {formatClock(remainingSec)}
            </p>
          </div>
        )}

        <Button
          tone={done ? 'gold' : 'ghost'}
          size="xl"
          block
          className="mt-6"
          disabled={!done}
          data-testid="break-back"
          onClick={() => void navigate({ to: '/arena' })}
        >
          {done ? 'Повернутись на арену' : 'Ще відпочиваємо…'}
        </Button>

        <SyncIndicator variant="compact" className="mt-4" />
      </Panel>
    </main>
  );
}

/**
 * Момент, коли можна далі. 0 — відпочивати не треба.
 *
 * Найчастіше це просто `breakUntil` із сервера. Але буває й вікно, де межа
 * відрізка вже позаду, а перерви ще немає: сервер призначає її за СВОЇМ
 * годинником, тож така відповідь означає, що вона старша за межу (між
 * оновленнями пів хвилини, а офлайн — скільки завгодно) або що наш годинник
 * трохи попереду. Сказати в цю мить «перерва скінчилась» — обман, тому
 * рахуємо стелю: перерву сервер відлічує від останньої спроби, а вона була
 * не пізніше за межу, отже відпустить він не пізніше ніж
 * `playUntil + breakMinutes`. Стеля завжди більша за правду, і щойно приїде
 * свіже вікно, відлік урветься сам — обіцянки «вже можна» наперед немає.
 */
function restEndsAt(playWindow: PlayWindow | undefined, now: number): number {
  if (!playWindow) return 0;
  if (playWindow.breakUntil) return new Date(playWindow.breakUntil).getTime();
  if (!playWindow.playUntil || playWindow.breakMinutes <= 0) return 0;
  const edge = new Date(playWindow.playUntil).getTime();
  return edge <= now ? edge + playWindow.breakMinutes * MINUTE_MS : 0;
}
